import {
	animate,
	state,
	style,
	transition,
	trigger
} from '@angular/animations';
import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ProfileMenuHeaderComponent } from '@brand-lolly/app/sidenav/sidenav/profile-menu-header.component';
import { IconComponent, TypographyComponent } from 'components';

@Component({
	standalone: true,
	selector: 'app-profile-menu',
	template: `
		<div *ngIf="opened" [@fadeInOut] class="menu-container">
			<app-profile-menu-header
				[fullName]="fullName"
				[email]="email"
				[image]="image"
				[imageLoading]="imageLoading"
			></app-profile-menu-header>

			<div class="item settings" (click)="goToSettings.emit()">
				<app-icon type="svg" [size]="20" color="#969696">
					regular_settings
				</app-icon>
				<typography variant="p/sm_medium">Settings</typography>
			</div>
			<!-- Only the account owner changes what the account pays, so a
			     seated member is not offered the way in. -->
			<div
				*ngIf="canManageBilling"
				class="item pricing"
				(click)="goToUpgrade.emit()"
			>
				<app-icon type="svg" [size]="20" color="#969696">
					dollar
				</app-icon>
				<typography variant="p/sm_medium">Pricing and plans</typography>
			</div>
			<div class="item tickets" (click)="goToTicketCreation.emit()">
				<app-icon type="svg" [size]="20" color="#969696">
					help_circle
				</app-icon>
				<typography variant="p/sm_medium">Create a Ticket</typography>
			</div>
			<div class="item logout" (click)="logout.emit()">
				<app-icon type="svg" [size]="20" color="#FF5656">
					logout
				</app-icon>
				<typography variant="p/sm_medium">Sign Out</typography>
			</div>
		</div>
	`,
	imports: [
		CommonModule,
		IconComponent,
		TypographyComponent,
		MatTooltipModule,
		ProfileMenuHeaderComponent
	],
	animations: [
		trigger('fadeInOut', [
			state(
				'void',
				style({
					opacity: 0,
					transform: 'translateY(-0.25rem)'
				})
			),
			state(
				'*',
				style({
					opacity: 1,
					transform: 'translateY(0)'
				})
			),
			transition('void => *', [animate('150ms ease-in-out')]),
			transition('* => void', [animate('150ms ease-in-out')])
		])
	],
	styles: [
		//language=scss
		`
			@use 'animations';

			:host {
				display: block;
				position: absolute;
				top: 2.25rem;
				right: 0.25rem;
				z-index: 10;

				.menu-container {
					display: flex;
					position: relative;
					width: 17.75rem;
					border-radius: 1rem 0 1rem 1rem;
					box-shadow: 1px 2px 9.6px 0 rgba(18, 18, 18, 0.2);
					flex-direction: column;
					background: #fff;
					overflow: hidden;
					border: 1px solid #dcdcdc;

					.item {
						display: flex;
						width: 100%;
						padding: 1rem;
						align-items: center;
						column-gap: 0.5rem;
						background: #fff;
						transition: background 150ms;
						cursor: pointer;

						typography {
							color: #7a7a7a;
						}

						&:hover {
							background: #fafafa;
						}

						&:not(:last-child) {
							border-bottom: 1px solid #dcdcdc;
						}

						typography {
							color: #7a7a7a;
						}

						&.logout {
							typography {
								color: #ff5656;
							}
						}

						app-icon {
							display: flex;
							align-items: center;
						}
					}
				}
			}
		`
	]
})
export class ProfileMenuComponent {
	@Input() opened = true;
	@Input() fullName: string;
	@Input() email: string;
	@Input() image: string;
	@Input() imageLoading: boolean = false;
	@Input() planName: string;
	@Input() loading: boolean = false;
	@Input() isTrial: boolean = false;
	@Input() resetTimerInSeconds: number;
	/** Whether this person may change what the account pays. Owner-only. */
	@Input() canManageBilling: boolean = true;

	@Output()
	goToSettings = new EventEmitter<void>();
	@Output() goToKnowledgeBase = new EventEmitter<void>();
	@Output() goToTicketCreation = new EventEmitter<void>();
	@Output() goToUpgrade = new EventEmitter<void>();
	@Output() logout = new EventEmitter<void>();
}
